import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Flag,
  Heart,
  ImageIcon,
  LayoutGrid,
  List,
  Loader2,
  MoreHorizontal,
  Pencil,
  PlusCircle,
  RefreshCw,
  Search,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import { useContentStore } from '@/store/contentStore';
import { GenerationStatusCell } from '@/components/GenerationStatusCell';
import { addDays, isValidDateStr, todayStr } from '@/utils/dateUtils';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';
import { CardTypeBadge, SourceLanguageBadge, StatusBadge, TranslationStatusBadge } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Alert, Skeleton, Tabs, TabsList, TabsTrigger } from '@/components/ui/misc';
import {
  CARD_TYPES,
  CARD_TYPE_LABELS,
  CARD_STATUSES,
  LANGUAGE_NAMES,
  sourceContentOf,
  type CardType,
  type CardStatus,
  type ContentCard,
} from '@/types/content';

// URL values for the date bar (VAP-823). Absent `date` param means "Showing now".
const ALL_DATES = 'all';
const SHOWING_NOW = 'showing';
// Radix Select cannot hold an empty-string value; this stands for "no filter".
const ANY = '__any__';
const VIEW_STORAGE_KEY = 'vapor-admin-content-view';

type ViewMode = 'grid' | 'table';

function isCardType(value: string): value is CardType {
  return (CARD_TYPES as readonly string[]).includes(value);
}

function isCardStatus(value: string): value is CardStatus {
  return (CARD_STATUSES as readonly string[]).includes(value);
}

function readStoredView(): ViewMode {
  try {
    return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'table' ? 'table' : 'grid';
  } catch {
    return 'grid';
  }
}

type PendingAction =
  | { kind: 'delete'; cardIds: string[] }
  | { kind: 'publish'; cardIds: string[] }
  | { kind: 'unpublish'; cardIds: string[] }
  | null;

export function ContentList() {
  const navigate = useNavigate();
  const {
    cards,
    selectedCards,
    loading,
    loadingMore,
    nextToken,
    error,
    fetchCards,
    loadMoreCards,
    toggleCardSelection,
    selectAllCards,
    clearSelection,
    bulkDeleteCards,
    clearError,
    retryTranslations,
    retryGeneration,
    stopPollingTranslations,
    stopPollingGeneration,
    updateCardStatus,
    bulkUpdateStatus,
  } = useContentStore();

  // Filters live in the URL so reload and Back keep them (VAP-823).
  // No `date` param = "Showing now": the cards the app shows today.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawDate = searchParams.get('date');
  const showingNow = rawDate === null || rawDate === SHOWING_NOW;
  const dateFilter: string | null =
    showingNow ? todayStr() : rawDate === ALL_DATES ? null : isValidDateStr(rawDate) ? rawDate : todayStr();
  const rawType = searchParams.get('type') ?? '';
  const typeFilter: CardType | '' = isCardType(rawType) ? rawType : '';
  // In the showing view every card is PUBLISHED, so a leftover ?status= param is ignored
  const rawStatus = showingNow ? '' : searchParams.get('status') ?? '';
  const statusFilter: CardStatus | '' = isCardStatus(rawStatus) ? rawStatus : '';

  const updateParams = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '' || value === ANY) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    setSearchParams(next);
  };

  const setDate = (date: string | null) => updateParams({ date: date === null ? ALL_DATES : date });
  const setShowingNow = () => updateParams({ date: null, status: null });
  const isToday = !showingNow && dateFilter === todayStr();

  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState<ViewMode>(readStoredView);
  const [pending, setPending] = useState<PendingAction>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [retryingCardId, setRetryingCardId] = useState<string | null>(null);
  const [retryingGenerationCardId, setRetryingGenerationCardId] = useState<string | null>(null);

  const changeView = (next: ViewMode) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // storage unavailable: the choice lasts for this tab only
    }
  };

  // Fetch whenever a URL filter changes; a change applies at once, no Apply button
  useEffect(() => {
    fetchCards({
      cardType: typeFilter || undefined,
      status: showingNow ? undefined : statusFilter || undefined,
      publishDate: showingNow ? undefined : dateFilter ?? undefined,
      showingNow: showingNow || undefined,
    });
  }, [fetchCards, typeFilter, statusFilter, dateFilter, showingNow]);

  // Stop polling when the page unmounts
  useEffect(() => {
    return () => {
      stopPollingTranslations();
      stopPollingGeneration();
    };
  }, [stopPollingTranslations, stopPollingGeneration]);

  // Search filters the loaded cards on the client
  const visibleCards = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return cards;
    }
    return cards.filter(card => {
      const text = sourceContentOf(card);
      const title = text?.title?.toLowerCase() || '';
      const summary = text?.summary?.toLowerCase() || '';
      const body = text?.body?.toLowerCase() || '';
      return title.includes(query) || summary.includes(query) || body.includes(query);
    });
  }, [cards, searchQuery]);

  // Clear search, type and status. The date stays; "All dates" is its own button.
  const handleClearFilters = () => {
    setSearchQuery('');
    updateParams({ type: null, status: null });
  };
  const hasClearableFilters = Boolean(typeFilter || statusFilter || searchQuery);

  const allVisibleSelected = visibleCards.length > 0 && visibleCards.every(c => selectedCards.has(c.cardId));
  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      clearSelection();
    } else if (visibleCards.length === cards.length) {
      selectAllCards();
    } else {
      visibleCards.forEach(c => {
        if (!selectedCards.has(c.cardId)) toggleCardSelection(c.cardId);
      });
    }
  };

  const selectedDraftCards = cards.filter(c => selectedCards.has(c.cardId) && c.status === 'DRAFT');
  const selectedPublishedCards = cards.filter(c => selectedCards.has(c.cardId) && c.status === 'PUBLISHED');

  // One confirm dialog drives every publish/unpublish/delete, single or bulk
  const runPending = async () => {
    if (!pending) return;
    setActionBusy(true);
    const count = pending.cardIds.length;
    const plural = count === 1 ? 'card' : 'cards';
    let ok = false;
    if (pending.kind === 'delete') {
      ok = await bulkDeleteCards(pending.cardIds);
      if (ok) toast.success(`Deleted ${count} ${plural}`);
    } else {
      const status: CardStatus = pending.kind === 'publish' ? 'PUBLISHED' : 'DRAFT';
      if (count === 1) {
        ok = (await updateCardStatus(pending.cardIds[0], status)) !== null;
      } else {
        ok = await bulkUpdateStatus(pending.cardIds, status);
      }
      if (ok) toast.success(`${pending.kind === 'publish' ? 'Published' : 'Unpublished'} ${count} ${plural}`);
    }
    setActionBusy(false);
    if (ok) setPending(null);
  };

  const handleRetryTranslations = async (cardId: string) => {
    setRetryingCardId(cardId);
    const ok = await retryTranslations(cardId);
    setRetryingCardId(null);
    if (ok) toast.success('Translation restarted');
  };

  const handleRetryGeneration = async (cardId: string) => {
    setRetryingGenerationCardId(cardId);
    const ok = await retryGeneration(cardId);
    setRetryingGenerationCardId(null);
    if (ok) toast.success('Generation restarted');
  };

  const headerSubtitle = (() => {
    const count = `${visibleCards.length} content card${visibleCards.length === 1 ? '' : 's'}`;
    if (showingNow) return `${count} showing in the app now`;
    if (dateFilter === null) return `${count}, all dates`;
    if (isToday) return `${count} published today, ${dateFilter}`;
    return `${count} for ${dateFilter}`;
  })();

  const rowActions = (card: ContentCard) => ({
    edit: () => navigate(`/content/${card.cardId}`),
    publish: () => setPending({ kind: 'publish', cardIds: [card.cardId] }),
    unpublish: () => setPending({ kind: 'unpublish', cardIds: [card.cardId] }),
    delete: () => setPending({ kind: 'delete', cardIds: [card.cardId] }),
    retryTranslations: () => handleRetryTranslations(card.cardId),
    retryGeneration: () => handleRetryGeneration(card.cardId),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Content"
        description={headerSubtitle}
        actions={
          <Button asChild>
            <Link to="/content/create">
              <PlusCircle />
              Create Content
            </Link>
          </Button>
        }
      />

      {error && (
        <Alert variant="error" title="Error" onDismiss={clearError}>
          {error}
        </Alert>
      )}

      {/* Filter bar */}
      <Card className="p-3 sm:p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          {/* Date bar (VAP-823) */}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant={showingNow ? 'default' : 'outline'} onClick={setShowingNow} aria-pressed={showingNow}>
              <Eye />
              Showing now
            </Button>
            <Button variant={isToday ? 'default' : 'outline'} onClick={() => setDate(todayStr())} aria-pressed={isToday}>
              <CalendarDays />
              Today
            </Button>
            <div className={cn(
              'inline-flex items-center overflow-hidden rounded-md border border-input bg-card shadow-sm',
              showingNow && 'opacity-70'
            )}>
              <button
                type="button"
                onClick={() => setDate(addDays(dateFilter ?? todayStr(), -1))}
                aria-label="Previous day"
                disabled={showingNow}
                className="p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <input
                type="date"
                aria-label="Publish date"
                disabled={showingNow}
                title={showingNow ? 'Pick Today or All dates to browse by date' : undefined}
                value={showingNow ? '' : dateFilter ?? ''}
                onChange={(e) => {
                  if (isValidDateStr(e.target.value)) setDate(e.target.value);
                }}
                className="h-9 border-x border-input bg-transparent px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <button
                type="button"
                onClick={() => setDate(addDays(dateFilter ?? todayStr(), 1))}
                aria-label="Next day"
                disabled={showingNow}
                className="p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <Button variant={dateFilter === null ? 'default' : 'outline'} onClick={() => setDate(null)} aria-pressed={dateFilter === null}>
              All dates
            </Button>
          </div>

          <div className="hidden flex-1 lg:block" />

          {/* Type and status: inline, apply at once */}
          <div className="flex flex-wrap items-center gap-2">
            <Select value={typeFilter || ANY} onValueChange={(v) => updateParams({ type: v })}>
              <SelectTrigger className="w-[160px]" aria-label="Card type">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All types</SelectItem>
                {CARD_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{CARD_TYPE_LABELS[type]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Every card showing in the app is PUBLISHED, so the status filter is hidden in that view */}
            {!showingNow && (
              <Select value={statusFilter || ANY} onValueChange={(v) => updateParams({ status: v })}>
                <SelectTrigger className="w-[150px]" aria-label="Status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>All statuses</SelectItem>
                  {CARD_STATUSES.map(status => (
                    <SelectItem key={status} value={status}>{status.charAt(0) + status.slice(1).toLowerCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {hasClearableFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                <X />
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-col gap-3 border-t border-border pt-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              type="search"
              placeholder="Search loaded cards..."
              aria-label="Search loaded cards"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                aria-label="Select all visible cards"
                checked={allVisibleSelected}
                onChange={toggleSelectAllVisible}
                disabled={visibleCards.length === 0}
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              Select all
            </label>
            <Tabs value={view} onValueChange={(v) => changeView(v as ViewMode)}>
              <TabsList aria-label="View">
                <TabsTrigger value="grid" aria-label="Grid view"><LayoutGrid /><span className="hidden sm:inline">Grid</span></TabsTrigger>
                <TabsTrigger value="table" aria-label="Table view"><List /><span className="hidden sm:inline">Table</span></TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </Card>

      {/* Bulk action bar */}
      {selectedCards.size > 0 && (
        <div className="sticky top-16 z-20 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary-50/90 px-4 py-2.5 shadow-sm backdrop-blur dark:bg-primary-900/40 lg:top-4">
          <span className="text-sm font-medium">{selectedCards.size} selected</span>
          <div className="flex-1" />
          {selectedDraftCards.length > 0 && (
            <Button size="sm" variant="success" onClick={() => setPending({ kind: 'publish', cardIds: selectedDraftCards.map(c => c.cardId) })}>
              <Send />
              Publish ({selectedDraftCards.length})
            </Button>
          )}
          {selectedPublishedCards.length > 0 && (
            <Button size="sm" variant="warning" onClick={() => setPending({ kind: 'unpublish', cardIds: selectedPublishedCards.map(c => c.cardId) })}>
              <EyeOff />
              Unpublish ({selectedPublishedCards.length})
            </Button>
          )}
          <Button size="sm" variant="destructive" onClick={() => setPending({ kind: 'delete', cardIds: Array.from(selectedCards) })}>
            <Trash2 />
            Delete ({selectedCards.size})
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      )}

      {/* Content */}
      {loading && cards.length === 0 ? (
        view === 'grid' ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-64" />)}
          </div>
        ) : (
          <Skeleton className="h-64" />
        )
      ) : visibleCards.length === 0 ? (
        <Card className="flex flex-col items-center justify-center px-6 py-16 text-center">
          <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <ImageIcon className="h-6 w-6" />
          </span>
          <p className="font-medium">
            {searchQuery
              ? 'No loaded cards match your search'
              : showingNow
                ? 'The app shows no cards right now'
                : dateFilter
                  ? `No content cards for ${dateFilter}`
                  : 'No content cards found'}
          </p>
          {showingNow && !searchQuery && (
            <Button variant="link" onClick={() => setDate(todayStr())}>Show every card dated today</Button>
          )}
          {!showingNow && dateFilter && !searchQuery && (
            <Button variant="link" onClick={() => setDate(null)}>Show all dates</Button>
          )}
          {!dateFilter && !searchQuery && (
            <Button variant="link" asChild><Link to="/content/create">Create your first card</Link></Button>
          )}
        </Card>
      ) : view === 'grid' ? (
        <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 transition-opacity', loading && 'opacity-60')} aria-busy={loading}>
          {visibleCards.map(card => (
            <ContentGridCard
              key={card.cardId}
              card={card}
              selected={selectedCards.has(card.cardId)}
              onToggleSelect={() => toggleCardSelection(card.cardId)}
              actions={rowActions(card)}
              retryingTranslations={retryingCardId === card.cardId}
              retryingGeneration={retryingGenerationCardId === card.cardId}
            />
          ))}
        </div>
      ) : (
        <Card className={cn('overflow-hidden transition-opacity', loading && 'opacity-60')} aria-busy={loading}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select all visible cards"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                    />
                  </th>
                  <th className="w-full px-4 py-3 font-medium">Content</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Generation</th>
                  <th className="px-4 py-3 font-medium">Translations</th>
                  <th className="px-4 py-3 font-medium">Metrics</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="w-12 px-2 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visibleCards.map(card => (
                  <tr key={card.cardId} className={cn('transition-colors hover:bg-muted/40', selectedCards.has(card.cardId) && 'bg-primary-50/60 dark:bg-primary-900/20')}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${sourceContentOf(card)?.title || 'card'}`}
                        checked={selectedCards.has(card.cardId)}
                        onChange={() => toggleCardSelection(card.cardId)}
                        className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                      />
                    </td>
                    <td className="w-full max-w-0 px-4 py-3">
                      <Link to={`/content/${card.cardId}`} className="group block">
                        <span className="flex items-center gap-2">
                          <span className="truncate font-medium group-hover:text-primary">{sourceContentOf(card)?.title || 'Untitled'}</span>
                          {card.isPushFlagged && <Flag className="h-3.5 w-3.5 shrink-0 text-accent" aria-label="Push flagged" />}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">{sourceContentOf(card)?.summary || ''}</span>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex flex-wrap items-center gap-1">
                        <CardTypeBadge cardType={card.cardType} />
                        <SourceLanguageBadge card={card} />
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground tabular-nums">{card.publishDate}</td>
                    <td className="px-4 py-3">
                      <GenerationStatusCell
                        status={card.generationStatus}
                        error={card.generationError}
                        onRetry={() => handleRetryGeneration(card.cardId)}
                        isRetrying={retryingGenerationCardId === card.cardId}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <TranslationCell card={card} onRetry={() => handleRetryTranslations(card.cardId)} isRetrying={retryingCardId === card.cardId} />
                    </td>
                    <td className="px-4 py-3">
                      <Metrics card={card} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3"><StatusBadge status={card.status} /></td>
                    <td className="px-2 py-3">
                      <RowMenu card={card} actions={rowActions(card)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Load more (VAP-823): the API returns at most 100 cards per page */}
      {nextToken && !loading && (
        <div className="flex items-center justify-center gap-3 py-2">
          <span className="text-sm text-muted-foreground">{cards.length} loaded</span>
          <Button variant="outline" onClick={() => loadMoreCards()} loading={loadingMore}>
            {loadingMore ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => { if (!open) setPending(null); }}
        title={pendingTitle(pending)}
        description={pendingDescription(pending)}
        confirmLabel={pending?.kind === 'delete' ? 'Delete' : pending?.kind === 'publish' ? 'Publish' : 'Unpublish'}
        confirmVariant={pending?.kind === 'delete' ? 'destructive' : pending?.kind === 'publish' ? 'success' : 'warning'}
        loading={actionBusy}
        onConfirm={runPending}
      />
    </div>
  );
}

function pendingTitle(pending: PendingAction): string {
  if (!pending) return '';
  const n = pending.cardIds.length;
  const noun = n === 1 ? 'this card' : `${n} cards`;
  if (pending.kind === 'delete') return `Delete ${noun}?`;
  if (pending.kind === 'publish') return `Publish ${noun}?`;
  return `Unpublish ${noun}?`;
}

function pendingDescription(pending: PendingAction): string {
  if (!pending) return '';
  if (pending.kind === 'delete') return 'This cannot be undone. The content and its uploaded images are removed.';
  if (pending.kind === 'publish') return 'The content becomes visible to all app users.';
  return 'The content is hidden from all app users and goes back to Draft.';
}

interface RowActions {
  edit: () => void;
  publish: () => void;
  unpublish: () => void;
  delete: () => void;
  retryTranslations: () => void;
  retryGeneration: () => void;
}

function RowMenu({ card, actions }: { card: ContentCard; actions: RowActions }) {
  const canRetryTranslations = card.translationStatus === 'PARTIAL' || card.translationStatus === 'FAILED';
  const canRetryGeneration = card.generationStatus === 'FAILED';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${sourceContentOf(card)?.title || 'card'}`}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={actions.edit}><Pencil />Edit</DropdownMenuItem>
        {card.status === 'DRAFT' && <DropdownMenuItem onSelect={actions.publish}><Send />Publish</DropdownMenuItem>}
        {card.status === 'PUBLISHED' && <DropdownMenuItem onSelect={actions.unpublish}><EyeOff />Unpublish</DropdownMenuItem>}
        {(canRetryTranslations || canRetryGeneration) && <DropdownMenuSeparator />}
        {canRetryGeneration && <DropdownMenuItem onSelect={actions.retryGeneration}><RefreshCw />Retry generation</DropdownMenuItem>}
        {canRetryTranslations && <DropdownMenuItem onSelect={actions.retryTranslations}><RefreshCw />Retry translations</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={actions.delete}><Trash2 />Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Metrics({ card }: { card: ContentCard }) {
  return (
    <span className="flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
      <span className="flex items-center gap-1" title="Fetches"><Eye className="h-3.5 w-3.5" aria-hidden />{card.metrics.fetchCount}</span>
      <span className="flex items-center gap-1" title="Likes"><Heart className="h-3.5 w-3.5" aria-hidden />{card.metrics.likeCount}</span>
    </span>
  );
}

function firstImage(card: ContentCard): string | undefined {
  return card.mediaUrls?.find(m => m.type === 'image')?.url ?? card.imageUrl ?? undefined;
}

interface ContentGridCardProps {
  card: ContentCard;
  selected: boolean;
  onToggleSelect: () => void;
  actions: RowActions;
  retryingTranslations: boolean;
  retryingGeneration: boolean;
}

function ContentGridCard({ card, selected, onToggleSelect, actions, retryingTranslations, retryingGeneration }: ContentGridCardProps) {
  const image = firstImage(card);
  const text = sourceContentOf(card);
  const title = text?.title || 'Untitled';
  const needsAttention = card.generationStatus === 'FAILED' || card.translationStatus === 'FAILED' || card.translationStatus === 'PARTIAL';

  return (
    <Card className={cn('group relative flex flex-col overflow-hidden transition-shadow hover:shadow-card-hover', selected && 'ring-2 ring-primary')}>
      {/* Thumbnail */}
      <Link to={`/content/${card.cardId}`} className="relative block aspect-[16/9] bg-muted">
        {image ? (
          <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground/60">
            <ImageIcon className="h-8 w-8" />
          </div>
        )}
        <div className="absolute left-2 top-2 flex items-center gap-1.5">
          <CardTypeBadge cardType={card.cardType} className="bg-card/90 backdrop-blur" />
          <SourceLanguageBadge card={card} className="bg-card/90 backdrop-blur" />
          {card.isPushFlagged && (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent text-white shadow" title="Push flagged">
              <Flag className="h-3 w-3" />
            </span>
          )}
        </div>
      </Link>

      {/* Select checkbox and menu, over the thumbnail */}
      <div className="absolute right-2 top-2 flex items-center gap-1">
        <label className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md bg-card/90 shadow backdrop-blur">
          <input
            type="checkbox"
            aria-label={`Select ${title}`}
            checked={selected}
            onChange={onToggleSelect}
            className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
          />
        </label>
        <div className="rounded-md bg-card/90 shadow backdrop-blur">
          <RowMenu card={card} actions={actions} />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="min-w-0">
          <Link to={`/content/${card.cardId}`} className="line-clamp-2 font-medium leading-snug hover:text-primary">
            {title}
          </Link>
          {text?.summary && (
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{text.summary}</p>
          )}
        </div>

        <div className="mt-auto flex flex-wrap items-center gap-2">
          <StatusBadge status={card.status} />
          <span className="text-xs text-muted-foreground tabular-nums">{card.publishDate}</span>
          <span className="flex-1" />
          <Metrics card={card} />
        </div>

        {(needsAttention || card.generationStatus === 'GENERATING' || card.translationStatus === 'TRANSLATING') && (
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
            {card.generationStatus !== 'NONE' && card.generationStatus !== 'GENERATED' && (
              <GenerationStatusCell
                status={card.generationStatus}
                error={card.generationError}
                onRetry={actions.retryGeneration}
                isRetrying={retryingGeneration}
              />
            )}
            {card.translationStatus && card.translationStatus !== 'NONE' && card.translationStatus !== 'TRANSLATED' && (
              <TranslationCell card={card} onRetry={actions.retryTranslations} isRetrying={retryingTranslations} compact />
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

interface TranslationCellProps {
  card: ContentCard;
  onRetry: () => void;
  isRetrying: boolean;
  compact?: boolean;
}

function TranslationCell({ card, onRetry, isRetrying, compact }: TranslationCellProps) {
  const { translationStatus, translationProgress } = card;

  if (!translationStatus || translationStatus === 'NONE') {
    return <span className="text-xs text-muted-foreground">N/A</span>;
  }

  // A Croatian card tracks `en` instead of `hr` (VAP-827): read the keys the card has
  const langs = translationProgress
    ? (Object.keys(translationProgress) as Array<keyof typeof LANGUAGE_NAMES>).filter(l => l in LANGUAGE_NAMES)
    : [];
  const completedCount = langs.filter(lang => translationProgress?.[lang] === 'completed').length;
  const failedCount = langs.filter(lang => translationProgress?.[lang] === 'failed').length;
  const canRetry = translationStatus === 'PARTIAL' || translationStatus === 'FAILED';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <TranslationStatusBadge status={translationStatus} />
        {canRetry && (
          <Button variant="ghost" size="sm" onClick={onRetry} disabled={isRetrying} className="h-6 px-1.5 text-xs">
            {isRetrying ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            Retry
          </Button>
        )}
      </div>
      {translationProgress && !compact && (
        <div className="flex items-center gap-1.5">
          <div className="flex gap-0.5">
            {langs.map(lang => (
              <span
                key={lang}
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  translationProgress[lang] === 'completed' ? 'bg-success' : translationProgress[lang] === 'failed' ? 'bg-destructive' : 'animate-pulse bg-muted-foreground/40'
                )}
                title={`${LANGUAGE_NAMES[lang]}: ${translationProgress[lang]}`}
              />
            ))}
          </div>
          <span className="text-xs text-muted-foreground tabular-nums">
            {completedCount}/{langs.length}
            {failedCount > 0 && <span className="text-destructive"> ({failedCount} failed)</span>}
          </span>
        </div>
      )}
    </div>
  );
}
